import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TL } from "../i18n";
import { useNavigate } from "react-router-dom";
import client from "../lib/api";
import { Bell, BellRing, Check, RotateCcw } from "lucide-react";

const DOT = { critical: "#DA1E28", warning: "#F1C21B", info: "var(--primary)" };

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const loadCount = () => client.get("/admin/alerts/unread-count").then((r) => setUnread(r.data.unread)).catch(() => {});
  const loadList = () => client.get("/admin/alerts", { params: { limit: 12 } }).then((r) => { setItems(r.data.items); setUnread(r.data.unread); }).catch(() => {});

  const place = useCallback(() => {
    const b = btnRef.current?.getBoundingClientRect();
    if (!b) return;
    const w = Math.min(384, window.innerWidth - 16);
    const left = Math.max(8, Math.min(b.left, window.innerWidth - w - 8));
    setPos({ top: b.bottom + 8, left });
  }, []);

  useEffect(() => { loadCount(); const id = setInterval(loadCount, 30000); return () => clearInterval(id); }, []);
  useEffect(() => { if (open) loadList(); }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    place();
    const on = () => place();
    window.addEventListener("resize", on);
    window.addEventListener("scroll", on, true);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("scroll", on, true); };
  }, [open, place]);
  useEffect(() => {
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const toggleRead = async (a) => { await client.post(`/admin/alerts/${a.id}/${a.read ? "unread" : "read"}`); loadList(); };
  const markAll = async () => { await client.post("/admin/alerts/read-all"); loadList(); };

  return (
    <div className="relative" data-testid="notifications-bell">
      <button ref={btnRef} onClick={() => setOpen((o) => !o)} data-testid="notifications-bell-btn"
        className="relative flex h-9 w-9 items-center justify-center rounded-full hover:bg-black/5 dark:hover:bg-white/10 lift">
        {unread > 0 ? <BellRing size={18} className="text-[#DA1E28]" /> : <Bell size={18} className="opacity-70" />}
        {unread > 0 && (
          <span data-testid="notifications-bell-count"
            className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#DA1E28] px-1 text-[10px] font-bold text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && createPortal(
        <div ref={panelRef} data-testid="notifications-panel"
          style={{ position: "fixed", top: pos.top, left: pos.left }}
          className="z-[1000] w-80 max-w-[92vw] overflow-hidden rounded-xl border border-black/10 bg-white shadow-2xl dark:border-white/10 dark:bg-[#161616] sm:w-96">
          <div className="flex items-center gap-2 border-b border-black/10 px-4 py-3 dark:border-white/10">
            <span className="font-head text-sm">{TL("Notifications")}</span>
            {unread > 0 && <span className="rounded-full bg-[#DA1E28] px-2 py-0.5 text-[10px] font-medium text-white">{unread} unread</span>}
            <button onClick={markAll} disabled={unread === 0} data-testid="notifications-mark-all"
              className="ms-auto text-xs text-[var(--primary)] hover:underline disabled:opacity-40">{TL("Mark all read")}</button>
          </div>
          <div className="max-h-96 overflow-y-auto" data-testid="notifications-list">
            {items.length === 0 && <div className="px-4 py-6 text-center text-sm opacity-50">{TL("No notifications.")}</div>}
            {items.map((a) => (
              <div key={a.id} data-testid={`notification-${a.id}`}
                className={`flex items-start gap-3 border-b border-black/5 px-4 py-3 last:border-0 dark:border-white/5 ${a.read ? "opacity-55" : ""}`}>
                <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full" style={{ background: DOT[a.severity] || DOT.info }} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium leading-snug">{a.title}</div>
                  <div className="text-xs opacity-70">{a.message}</div>
                  <div className="mt-1 text-[11px] opacity-40">{(a.created_at || "").slice(0, 16).replace("T", " ")} UTC</div>
                </div>
                <button onClick={() => toggleRead(a)} data-testid={`notification-toggle-${a.id}`} title={a.read ? "Mark unread" : "Mark read"}
                  className="flex-shrink-0 rounded-md border border-black/15 p-1.5 dark:border-white/15 lift">
                  {a.read ? <RotateCcw size={12} /> : <Check size={12} />}
                </button>
              </div>
            ))}
          </div>
          <button onClick={() => { setOpen(false); navigate("/app"); }} data-testid="notifications-view-all"
            className="block w-full border-t border-black/10 px-4 py-2.5 text-center text-xs text-[var(--primary)] hover:underline dark:border-white/10">
            View full alerts log →
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}
